import React from 'react';
import { Routes, Route } from 'react-router-dom';
import Home from '../pages/Home';
import QuickEstimate from '../pages/QuickEstimate';
import ComingSoon from '../pages/ComingSoon';
import Login from '../pages/auth/Login';
import Register from '../pages/auth/Register';
import ForgotPassword from '../pages/auth/ForgotPassword';
import Dashboard from '../pages/dashboard/Dashboard';
import Profile from '../pages/profile/Profile';
import AdminDashboard from '../pages/admin/AdminDashboard';
import DetailedEstimate from '../components/estimation/detailed/DetailedEstimate';
import AIAssistant from '../pages/estimation/AIAssistant';
import ViewEstimate from '../pages/estimates/ViewEstimate';
import EditEstimate from '../pages/estimates/EditEstimate';
import PurchaseCredits from '../pages/credits/PurchaseCredits';
import Billing from '../pages/credits/Billing';
import PaymentConfirmation from '../pages/credits/PaymentConfirmation';
import Contact from '../pages/Contact';
import AuthGuard from '../components/auth/AuthGuard';
import AdminGuard from '../components/admin/AdminGuard';
import LegalNotice from '../pages/LegalNotice';
import AppLayout from '../components/layout/AppLayout';
import AuthenticatedLayout from '../components/layout/AuthenticatedLayout';
import { useAuth } from '../contexts/AuthContext';

const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <AppLayout>{children}</AppLayout>
);

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <AuthGuard>
    <AuthenticatedLayout>{children}</AuthenticatedLayout>
  </AuthGuard>
);

// Renders AuthenticatedLayout for logged-in users, AppLayout for anonymous users.
// Used for /quick-estimate so the sidebar stays visible after login.
const ConditionalRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <AuthenticatedLayout>{children}</AuthenticatedLayout>;
  return <AppLayout>{children}</AppLayout>;
};

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Public routes — Navbar horizontale + Footer */}
      <Route path="/" element={<PublicRoute><Home /></PublicRoute>} />
      <Route path="/quick-estimate" element={<ConditionalRoute><QuickEstimate /></ConditionalRoute>} />
      <Route path="/coming-soon" element={<PublicRoute><ComingSoon /></PublicRoute>} />
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
      <Route path="/contact" element={<PublicRoute><Contact /></PublicRoute>} />
      <Route path="/mentions-legales" element={<PublicRoute><LegalNotice /></PublicRoute>} />

      {/* Protected routes — sidebar layout */}
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/estimation/assistant" element={<ProtectedRoute><AIAssistant /></ProtectedRoute>} />
      <Route path="/estimation/nouvelle" element={<ProtectedRoute><DetailedEstimate /></ProtectedRoute>} />
      <Route path="/estimation/:id/view" element={<ProtectedRoute><ViewEstimate /></ProtectedRoute>} />
      <Route path="/estimation/:id/edit" element={<ProtectedRoute><EditEstimate /></ProtectedRoute>} />
      <Route path="/credits/purchase" element={<ProtectedRoute><PurchaseCredits /></ProtectedRoute>} />
      <Route path="/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
      <Route path="/credits/confirmation" element={<ProtectedRoute><PaymentConfirmation /></ProtectedRoute>} />

      {/* Admin routes */}
      <Route path="/admin" element={<AdminGuard><AuthenticatedLayout><AdminDashboard /></AuthenticatedLayout></AdminGuard>} />
    </Routes>
  );
};

export default AppRoutes;
